import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CaretRight } from "components/icons/CaretRight";
import { RECOGNITION_STATUS } from "constants/deferredRecognition";
import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { InvoiceItem } from "types/Models/invoice";
import { VendorBill } from "types/Models/vendorBills";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";

type ExpenseScheduleEntry = NonNullable<
  InvoiceItem["expense_schedule"]
>[number];

export const BillItemExpenseScheduleDetails = ({
  billItem,
}: {
  billItem: InvoiceItem;
}) => {
  const columnHelper = createColumnHelper<ExpenseScheduleEntry>();

  const columns = [
    columnHelper.accessor("period", {
      header: "Period",
      cell: (info) => info.getValue(),
      size: 25,
    }),

    columnHelper.accessor("amount", {
      header: () => <div className="t-text-right">Recognized Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />
        </div>
      ),
      size: 45,
    }),

    columnHelper.accessor("status", {
      header: () => <div className="t-text-right">Status</div>,
      cell: (info) => (
        <div className="t-text-right">
          <Tag
            transparent
            tagType={
              info.getValue() === RECOGNITION_STATUS.RECOGNIZED
                ? "green"
                : "yellow"
            }
          >
            {capitalizeFirstLetter(info.getValue().toLowerCase())}
          </Tag>
        </div>
      ),
      size: 30,
    }),
  ];

  const data = useMemo(
    () => billItem.expense_schedule || [],
    [billItem.expense_schedule]
  );

  const table = useReactTable({
    data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return <TableUI sticky={false} table={table} size="regular" />;
};

export const AllBillExpenseScheduleDetails = ({
  bill,
}: {
  bill: VendorBill;
}) => {
  const itemsWithExpenseSchedule = bill.invoice_items.filter(
    (item) => item.expense_schedule && item.expense_schedule.length > 0
  );

  if (itemsWithExpenseSchedule.length === 0) {
    return null;
  }

  return (
    <Accordion.Root type="single" collapsible>
      <Accordion.Item value="EXPENSE_SCHEDULE_DETAILS">
        <Accordion.Trigger>Expense Recognition</Accordion.Trigger>
        <Accordion.Content>
          <Accordion.Root
            defaultValue={["item-0"]}
            type="multiple"
            className="t-flex t-flex-col t-gap-5"
          >
            {itemsWithExpenseSchedule.map((item, index) => (
              <Accordion.RawItem value={`item-${index}`} key={index}>
                <Accordion.RawTrigger asChild>
                  <div className="t-flex t-gap-2 t-items-center t-text-text-100 t-group">
                    <span className="group-data-state-open:t-rotate-90">
                      <CaretRight strokeWidth="1.4" />
                    </span>
                    <div className="t-text-body-sm t-leading-5 t-font-medium">
                      {item.description}
                    </div>
                  </div>
                </Accordion.RawTrigger>
                <Accordion.RawContent>
                  <BillItemExpenseScheduleDetails billItem={item} />
                </Accordion.RawContent>
              </Accordion.RawItem>
            ))}
          </Accordion.Root>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
